import type {
  AccessRole,
  CreateAccessRoleRequest,
  UpdateAccessRoleRequest,
} from '@billx/types';

import { userApiClient } from '../clients';
import type { NucleusServiceResponse } from './types';
import { handleAxiosError } from './utils';

export class AccessRoleService {
  /**
   * Create a new access role
   */
  static async createAccessRole(
    accessRoleData: CreateAccessRoleRequest
  ): Promise<NucleusServiceResponse<AccessRole>> {
    try {
      const response = await userApiClient.post(
        '/access-roles',
        accessRoleData
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to create access role');
    }
  }

  /**
   * Fetch access role relationships with filters
   */
  static async fetchCurrentUserAccessRoles(): Promise<
    NucleusServiceResponse<AccessRole[]>
  > {
    try {
      const response = await userApiClient.get('/user/access-roles');

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch access roles');
    }
  }

  /**
   * Fetch access role relationships by business ID
   */
  static async fetchAccessRolesByBusinessId(
    businessId: string
  ): Promise<NucleusServiceResponse<AccessRole[]>> {
    try {
      const params = new URLSearchParams();
      params.append('businessId', businessId);
      const response = await userApiClient.get(`/access-roles`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch access roles');
    }
  }

  /**
   * Fetch access role relationships by business ID
   */
  static async fetchAccessRoleByUserIdAndBusinessId(
    userId: string,
    businessId: string
  ): Promise<NucleusServiceResponse<AccessRole>> {
    try {
      const response = await userApiClient.get(
        `/user/access-roles/${userId}/business/${businessId}`
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch access roles');
    }
  }

  /**
   * Update a access role relationship
   */
  static async updateAccessRole(
    accessRoleId: string,
    updateData: UpdateAccessRoleRequest
  ): Promise<NucleusServiceResponse<AccessRole>> {
    try {
      const response = await userApiClient.patch(
        `/access-roles/${accessRoleId}`,
        updateData
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update access role');
    }
  }

  /**
   * Delete a access role relationship
   */
  static async deleteAccessRole(
    accessRoleId: string
  ): Promise<NucleusServiceResponse<AccessRole>> {
    try {
      const response = await userApiClient.delete(
        `/access-roles/${accessRoleId}`
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to delete access role');
    }
  }

  /**
   * Grant access to a user
   */
  static async grantAccess(
    request: CreateAccessRoleRequest
  ): Promise<NucleusServiceResponse<AccessRole>> {
    return this.createAccessRole(request);
  }
}
